import Button from "../../../components/Button";
import ProgressBar from "../../../components/ProgressBar";
import AddIcon from "/public/images/icons/add.svg?react";
import { useNavigate } from "react-router-dom";
import { useDrawerContext } from "../../../context/drawerContext";
import CloseIcon from "/public/images/icons/close.svg?react";
import TextInput from "../../../components/TextInput";
import { useState } from "react";
import MedicalCard from "../../../components/MedicalCard";
import PropTypes from "prop-types";

export default function Step5({ band, profile, medications }) {
  const navigate = useNavigate();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const [updatedMedication, setUpdatedMedication] = useState( JSON.parse(localStorage.getItem("activeBandStep5")) || medications || [] );
  const [deletedMedication, setDeletedMedication] = useState(localStorage.getItem("activeBandStep5Delete") ? JSON.parse(localStorage.getItem("activeBandStep5Delete")) : []);

  const openMedicalDrawer = (index) => {
    // Provide a unique key based on index to force re-render
    setOpen(true);
    setDrawerContent(
      <div className="w-full" key={`drawer-${index}`}>
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          MEDICATION
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px]">
            {index < updatedMedication.length ? "Edit your medication" : "Enter your medication name"}
          </h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            You can update your medication details anytime from your profile page.
          </p>
          <form
            onSubmit={(e) => index < updatedMedication.length ? editMedication(e, index) : addMedication(e)}
            className="flex flex-col gap-6"
          >
            <TextInput
              defaultValue={index < updatedMedication.length ? updatedMedication[index]?.name || updatedMedication[index]?.MedicationName || "" : ""}
              label={"Medication Name"}
              name={`medicationName${index}`}
              required={true}
            />
            <TextInput
              defaultValue={index < updatedMedication.length ? updatedMedication[index]?.dosage || updatedMedication[index]?.Dosage || "" : ""}
              label="Dosage"
              name={`dosage${index}`}
              required={true}
            />
            <TextInput
              defaultValue={index < updatedMedication.length ? updatedMedication[index]?.frequency || updatedMedication[index]?.Frequency || "" : ""}
              label="Frequency"
              name={`frequency${index}`}
              required={true}
            />
            <TextInput
              defaultValue={index < updatedMedication.length ? updatedMedication[index]?.additionalInformation || updatedMedication[index]?.Notes || "" : ""}
              name={`additionalInformation${index}`}
              label="Additional Information"
            />
            {index < updatedMedication.length && (
              <TextInput
                type="hidden"
                key={"editDateAdded" + index}
                defaultValue={updatedMedication[index]?.dateAdded || updatedMedication[index]?.DateAdded}
                name="dateAdded"
              />
            )}
            <Button submit className="!text-[#101820]">
              Confirm & Continue
            </Button>
          </form>
        </div>
      </div>
    );
  };

  const addMedication = (event) => {
    event.preventDefault();
    const newDetails = new FormData(event.currentTarget);

    const medicationName = newDetails.get(`medicationName${updatedMedication.length}`)?.trim();
    const medicationDosage = newDetails.get(`dosage${updatedMedication.length}`)?.trim();
    const medicationFrequency = newDetails.get(`frequency${updatedMedication.length}`)?.trim();

    if (!medicationName || !medicationDosage || !medicationFrequency) {
      return;
    }

    setUpdatedMedication((prevMedication) => [
      ...prevMedication,
      {
        medicationName,
        medicationDosage,
        medicationFrequency,
        additionalInformation: newDetails.get(`additionalInformation${updatedMedication.length}`),
        dateAdded: new Date(),
        ongoing: false,
      },
    ]);
    setOpen(false);
  };

  const editMedication = (event, index) => {
    event.preventDefault();
    const newDetails = new FormData(event.currentTarget);

    setUpdatedMedication((prevMedication) =>
      prevMedication.map((item, i) =>
        i === index
          ? {
              ...item,
              name: newDetails.get(`medicationName${index}`),
              dosage: newDetails.get(`dosage${index}`),
              frequency: newDetails.get(`frequency${index}`),
              additionalInformation: newDetails.get(`additionalInformation${index}`),
              dateAdded: newDetails.get(`dateAdded${index}`),
            }
          : item
      )
    );
    setOpen(false);
  };

  const removeMedication = (e, index) => {
    e.preventDefault(); // Prevent form submission

    setUpdatedMedication((prevMedication) => {
      const medication = prevMedication[index];

      if (medication.MedicationId) {
        setDeletedMedication((prevDeleted) => {
          // Check if the item already exists in deletedMedicalHistory to avoid adding it twice
          if (!prevDeleted.some(item => item.MedicationId === medication.MedicationId)) {
            return [...prevDeleted, medication];
          }
          return prevDeleted;
        });
      }

      return prevMedication.filter((_, i) => i !== index);
    });
  };

  const checkOngoing = (index) => {
    setUpdatedMedication((prevMedication) =>
      prevMedication.map((item, i) =>
        i === index
          ? {
              ...item,
              ongoing: !item.ongoing,
            }
          : item
      )
    );
  };

  const validateStep5 = () => {
    localStorage.setItem("activeBandStep5", JSON.stringify(updatedMedication));
    localStorage.setItem("activeBandStep5Delete", JSON.stringify(deletedMedication));
    navigate("/add-band/complete", { state: { band, profile } });
  };

  return (
    <div className="w-full min-h-svh flex flex-col justify-stretch mb-[50px] bg-[#F8F8F8]">
      <div className="p-[24px] flex flex-col gap-6 bg-white">
        <ProgressBar step={5} />
        <span className="tracking-[0.25em] text-[12px] uppercase font-semibold">
          Step 5
        </span>
        <h2>Please let us know if you take medication</h2>
        <p>
          This information will be visible to venue staff and the public when your band is unlocked.
        </p>
      </div>
      {updatedMedication.length > 0 && (
        <div className={`bg-sportsLightGrey border-[#E3E3E3] border-t-[1px] p-[24px] flex justify-center flex-col gap-6 ${updatedMedication.length <= 1 ?'pb-[250px]' : 'pb-[200px]'}`}>
          {updatedMedication.map((item, index) => {
            return (
            <MedicalCard
              editFunc={() => openMedicalDrawer(index)}
              checkedFunc={() => checkOngoing(index)}
              removeFunc={(e) => removeMedication(e, index)}
              key={index}
              medication={item}
              type="medication"
            />
          )})}
        </div>
      )}
      <div className="border-dashed border-t border-[#E3E3E3]" />
      <div className="fixed bottom-[60px] left-1/2 transform -translate-x-1/2 w-full max-w-[430px] border-t border-l border-r rounded-t-[32px] border-[#E3E3E3] bg-white p-[24px] flex flex-col justify-stretch gap-4">
        <Button onClick={() => openMedicalDrawer(updatedMedication.length)} variant="tertiary">
          <AddIcon className="w-[15px]" />
          Add Medication
        </Button>
        <Button onClick={validateStep5}>
          {updatedMedication.length > 0
            ? "Confirm & Continue"
            : "Continue Without Medication"}
        </Button>
      </div>
    </div>
  );
}

Step5.propTypes = {
  band: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
};
